import GuideLayout, {
  H2,
  H3,
  P,
  UL,
  Callout,
  ExternalLink,
  Strong,
  Em,
} from "../ui/GuideLayout.jsx";
import { Link } from "react-router-dom";

export default function TiposDeVisto() {
  return (
    <GuideLayout
      title="Trabalhar legal no Japão: os tipos de visto"
      subtitle="Quais vistos permitem trabalho no Japão, qual é o seu, e quais são as restrições reais de cada um. Guia para descendentes japoneses, cônjuges, trabalhadores qualificados e operários."
      slug="trabalhar-legal-japao-tipos-de-visto"
      description="Tipos de visto para trabalhar legalmente no Japão. Long-term resident, spouse, engineer, especificadas, descendente japonês, restrições e benefícios."
      updated="Maio de 2026"
      readTime="11 min"
    >
      <P>
        Quem mora no Japão precisa de um <Em>status de residência</Em> que
        defina exatamente que tipo de trabalho pode fazer. Trabalhar fora do
        que seu visto permite é crime — leva à perda do visto e deportação.
      </P>
      <P>
        Este guia explica os tipos de visto que mais aparecem na vida do
        brasileiro, com foco no que cada um deixa fazer e no que não deixa.
      </P>

      <H2 id="categoria-base">A grande divisão: visto de trabalho vs status sem restrição</H2>
      <P>
        Os vistos do Japão dividem-se em dois grandes grupos:
      </P>
      <UL>
        <li>
          <Strong>Status de atividade (活動類型 / katsudou ruikei):</Strong>{" "}
          definem QUAL trabalho você pode fazer. Engineer, specialist, skilled
          worker, intra-company transferee. Mudar de profissão = mudar de visto.
        </li>
        <li>
          <Strong>Status pessoal (身分類型 / mibun ruikei):</Strong> baseados
          em quem você é, não no que faz. Permanente, long-term resident,
          cônjuge de japonês, filho de japonês. <Strong>Sem restrição</Strong>{" "}
          de profissão.
        </li>
      </UL>
      <Callout>
        <Strong>Implicação prática enorme:</Strong> se você tem status pessoal
        (eijuusha, teijuusha, spouse), pode trabalhar em qualquer coisa —
        fábrica, restaurante, escritório, autônomo. Se tem status de atividade,
        está limitado à atividade do seu visto.
      </Callout>

      <H2 id="descendente-japones">Descendente japonês (Nikkei brasileiro)</H2>

      <H3>Long-term resident (定住者 / teijuusha)</H3>
      <P>
        O visto da maioria absoluta dos brasileiros nikkei. Tradicionalmente
        chamado de &ldquo;visto de descendente&rdquo;. Características:
      </P>
      <UL>
        <li><Strong>Trabalho livre</Strong>, sem restrição de profissão</li>
        <li>Validade tipicamente 1, 3 ou 5 anos, renovável</li>
        <li>Cônjuge e filhos do teijuusha podem vir junto, com mesmo status</li>
        <li>Caminho pra permanente: 5 anos contínuos com bom histórico</li>
      </UL>
      <P>
        Quem se qualifica:
      </P>
      <UL>
        <li>Filho de japonês (1ª geração / issei) com origem documentada</li>
        <li>Neto de japonês (2ª geração / nissei) com documentação</li>
        <li>Bisneto de japonês (3ª geração / sansei) — regra ampliada em 2017</li>
        <li>Cônjuge ou filho menor de qualquer um acima</li>
      </UL>
      <P>
        Documentação típica:
      </P>
      <UL>
        <li>Koseki touhon do antepassado japonês (certidão de nascimento japonesa)</li>
        <li>Cadeia de certidões de nascimento conectando você ao antepassado</li>
        <li>Tudo traduzido para japonês por tradutor reconhecido</li>
      </UL>
      <Callout>
        Se você tem origem japonesa mas não consegue comprovar com documento,
        existe processo via consulado para reconstruir a linhagem. Demora
        anos, mas é possível.
      </Callout>

      <H2 id="conjuge">Cônjuge de japonês ou permanente</H2>

      <H3>Spouse of Japanese national (日本人の配偶者等 / nihonjin no haigusha tou)</H3>
      <P>
        Para quem se casou com cidadão japonês. <Strong>Trabalho
        ilimitado</Strong>, qualquer profissão. Caminho rápido pra permanente
        (3 anos de casamento + 1 ano residindo no Japão).
      </P>

      <H3>Spouse of permanent resident (永住者の配偶者等)</H3>
      <P>
        Para cônjuge de quem já tem visto permanente. Mesmas vantagens do
        spouse of Japanese.
      </P>

      <Callout kind="warn">
        Casar só pelo visto é considerado fraude pela imigração. Eles
        investigam relacionamentos suspeitos (idade muito diferente, sem
        coabitação real, sem comunicação prévia em comum). Casos
        identificados levam a deportação e proibição de re-entrada.
      </Callout>

      <H2 id="trabalho-qualificado">Visto de trabalho qualificado</H2>

      <H3>Engineer / Specialist in humanities / International services</H3>
      <P>
        Conhecidos como <Em>gijinkoku</Em> (技人国 — encurtado). Para quem
        tem curso superior e trabalha em área técnica, administrativa ou
        internacional. Exemplos:
      </P>
      <UL>
        <li>Engenheiro de qualquer área</li>
        <li>Programador, desenvolvedor de software</li>
        <li>Tradutor, intérprete</li>
        <li>Designer, arquiteto</li>
        <li>Consultor de empresa internacional</li>
        <li>Marketing internacional</li>
      </UL>
      <P>
        Restrição: só pode trabalhar dentro da sua especialidade. Engenheiro
        não pode virar atendente de loja com esse visto. Mudar de área =
        mudar de visto.
      </P>

      <H3>Highly Skilled Professional (高度専門職)</H3>
      <P>
        Visto criado em 2015 pra atrair profissionais top. Sistema de pontos
        baseado em educação, experiência, salário, idade, japonês falado.
        Quem atinge 70 ou 80 pontos ganha benefícios:
      </P>
      <UL>
        <li>Permanente em apenas 1 ano (80 pontos) ou 3 anos (70 pontos)</li>
        <li>Família pode trabalhar livremente</li>
        <li>Possibilidade de trazer pais (cuidando de filhos pequenos)</li>
        <li>Possibilidade de trazer empregada doméstica</li>
      </UL>

      <H2 id="ssw-tg">Vistos pra trabalhador médio: SSW e TG</H2>

      <H3>Specified Skilled Worker (特定技能 / tokutei ginou)</H3>
      <P>
        Criado em 2019 para preencher escassez de trabalhador em setores
        específicos. Aceita brasileiros (e outras nacionalidades) sem
        precisar de curso superior. 14 setores:
      </P>
      <UL>
        <li>Construção</li>
        <li>Cuidados (kaigo — idosos)</li>
        <li>Manutenção de prédios</li>
        <li>Construção naval</li>
        <li>Manutenção de automóveis</li>
        <li>Aviação</li>
        <li>Hotelaria</li>
        <li>Agricultura</li>
        <li>Pesca</li>
        <li>Alimentos e bebidas (processamento)</li>
        <li>Serviços de restaurante</li>
        <li>Materiais industriais (metal, máquinas)</li>
        <li>Manufatura geral</li>
        <li>Eletricidade/eletrônica/informação</li>
      </UL>
      <P>
        Requisitos:
      </P>
      <UL>
        <li>Passar em prova de habilidade técnica da área</li>
        <li>Passar em prova de japonês (JLPT N4 ou JFT-Basic)</li>
        <li>Contrato com empresa qualificada</li>
      </UL>
      <P>
        Duas categorias:
      </P>
      <UL>
        <li>
          <Strong>SSW 1:</Strong> 5 anos máximo, não pode trazer família
        </li>
        <li>
          <Strong>SSW 2:</Strong> renovável sem limite, pode trazer família,
          caminho pra permanente. Disponível em mais setores agora.
        </li>
      </UL>

      <H3>Technical Intern Training (技能実習 / ginou jisshu) — sendo renomeado</H3>
      <P>
        Programa antigo de &ldquo;estágio técnico&rdquo;, muito criticado por
        abusos. Está sendo substituído pelo novo <Em>Ikusei Shuro</Em> (育成
        就労) a partir de 2027. Tipicamente para nacionalidades asiáticas
        (Vietnã, Filipinas, Indonésia) — pouco usado por brasileiros.
      </P>

      <H2 id="estudo">Visto de estudo</H2>

      <H3>Student visa (留学 / ryuugaku)</H3>
      <P>
        Para fazer faculdade, pós-graduação ou escola de japonês.
        Características:
      </P>
      <UL>
        <li>Permite trabalho parcial até 28 horas/semana com autorização (資格外活動)</li>
        <li>Renovação anual, até 2 anos por curso</li>
        <li>Termina o curso → precisa mudar de visto (trabalho geralmente)</li>
        <li>Pode dar continuidade se conseguir emprego ao formar (会社探し / job-hunting)</li>
      </UL>

      <H2 id="permanente">Permanente (永住者 / eijuusha)</H2>
      <P>
        O objetivo de muito brasileiro. Sem restrição de profissão, sem
        prazo de validade do status, sem precisar de empregador. O cartão
        zairyu renova a cada 7 anos, mas o status é eterno.
      </P>

      <H3>Requisitos típicos para aplicar</H3>
      <UL>
        <li>
          <Strong>10 anos contínuos no Japão</Strong>, com pelo menos 5 deles
          em visto de trabalho ou pessoal qualificado (não estudo)
        </li>
        <li>
          <Strong>Bom histórico</Strong>: sem crime, sem violação de imigração,
          sem atraso de imposto ou previdência
        </li>
        <li>
          <Strong>Estabilidade financeira</Strong>: renda estável, idealmente
          ¥3 milhões+ anuais
        </li>
        <li>
          <Strong>Garantidor</Strong> (geralmente permanente ou japonês)
        </li>
      </UL>

      <H3>Caminhos rápidos para permanente</H3>
      <UL>
        <li>
          <Strong>Casamento com japonês:</Strong> 3 anos de casamento + 1 ano
          no Japão (e o casamento pra valer)
        </li>
        <li>
          <Strong>Casamento com permanente:</Strong> regra similar
        </li>
        <li>
          <Strong>Highly Skilled Professional 80 pts:</Strong> 1 ano
        </li>
        <li>
          <Strong>Highly Skilled Professional 70 pts:</Strong> 3 anos
        </li>
        <li>
          <Strong>Filho de permanente nascido no Japão:</Strong> ao nascer
        </li>
      </UL>

      <H3>Cuidado: aperto recente nas regras de permanente</H3>
      <P>
        Em 2024-2025 entraram em vigor regras mais rígidas para permanente:
      </P>
      <UL>
        <li>
          <Strong>Revogação possível</Strong> por atraso de imposto ou
          previdência (não era assim antes)
        </li>
        <li>
          Renovação do cartão a cada 7 anos checa essas obrigações
        </li>
      </UL>
      <P>
        Se você quer aplicar, mantenha juushinkoku e nenkin em dia há pelo
        menos 2 anos antes de aplicar.
      </P>

      <H2 id="dependente">Visto de dependente</H2>
      <P>
        Veja o{" "}
        <Link to="/guias/trazer-familia-para-o-japao" className="text-accent underline">
          guia específico
        </Link>{" "}
        sobre como trazer família. Resumo:
      </P>
      <UL>
        <li>
          <Strong>Kazoku taizai</Strong> (家族滞在): cônjuge ou filho de
          trabalhador. Trabalho até 28h/sem.
        </li>
        <li>
          <Strong>Spouse of permanent resident</Strong>: trabalho livre.
        </li>
        <li>
          <Strong>Filho de long-term resident</Strong>: long-term resident
          (trabalho livre).
        </li>
      </UL>

      <H2 id="qual-eh-o-meu">Como saber qual é o seu</H2>
      <P>
        Olhe seu zairyu card. Na frente, abaixo do nome, está escrito o
        status em japonês:
      </P>
      <UL>
        <li>定住者 → Long-term resident (trabalho livre)</li>
        <li>永住者 → Permanente</li>
        <li>日本人の配偶者等 → Spouse of Japanese national</li>
        <li>永住者の配偶者等 → Spouse of permanent resident</li>
        <li>家族滞在 → Dependent (trabalho 28h/sem)</li>
        <li>技術・人文知識・国際業務 → Engineer/specialist (limitado à área)</li>
        <li>特定技能 → Specified Skilled Worker</li>
        <li>留学 → Student</li>
      </UL>

      <H2 id="proibicoes">Atividades proibidas para quase todos</H2>
      <P>
        Independente do tipo de visto, exceto permanente/teijuusha/spouse,
        é proibido:
      </P>
      <UL>
        <li>
          Trabalhar em setor do entretenimento adulto (mizu shobai, host
          club, sex industry)
        </li>
        <li>Trabalhar mais que o limite do seu visto (28h pra estudantes/dependentes)</li>
        <li>Pegar dinheiro do governo japonês (welfare/seikatsuhogo) sem qualificação extrema</li>
        <li>Trabalhar fora da sua área qualificada (pra vistos de atividade)</li>
      </UL>
      <P>
        Violar essas regras pode levar a:
      </P>
      <UL>
        <li>Multa de até ¥3 milhões</li>
        <li>Não renovação de visto</li>
        <li>Deportação</li>
        <li>Proibição de re-entrada por 5-10 anos</li>
      </UL>

      <H2 id="conclusao">Conclusão</H2>
      <P>
        O tipo de visto define o que você pode fazer no Japão. Para
        brasileiros, os mais comuns são:
      </P>
      <UL>
        <li><Strong>Teijuusha</Strong> (long-term, descendente): trabalho livre, renovável</li>
        <li><Strong>Eijuusha</Strong> (permanente): trabalho livre, status eterno</li>
        <li><Strong>Spouse</Strong>: trabalho livre, caminho rápido pra permanente</li>
        <li><Strong>Engineer/specialist</Strong>: limitado à área qualificada</li>
        <li><Strong>Kazoku taizai</Strong>: dependente, 28h/sem máximo</li>
      </UL>
      <P>
        Para renovação ou mudança de status, veja o{" "}
        <Link to="/guias/renovacao-visto-zairyu-card" className="text-accent underline">
          guia de renovação
        </Link>
        . Para casos complexos (mudança de categoria, aplicação de
        permanente), considere contratar um gyouseishoshi.
      </P>
    </GuideLayout>
  );
}
